import React, { ChangeEvent, ComponentPropsWithRef, FocusEvent, forwardRef, useEffect, useId, useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import cn from "@/utils/cn";

import Label from "../Label/Label";

export interface TextareaProps extends ComponentPropsWithRef<"textarea"> {
  name: string;
  label?: string;
  note?: string;
  error?: boolean;
  errorMessage?: string;
  reverse?: boolean;
  field?: ControllerRenderProps;
  fieldState?: ControllerFieldState;
  wrapperClassNames?: string;
}

const TextareaBase = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ name, label, note, error, errorMessage, reverse, field, fieldState, wrapperClassNames, ...rest }, ref) => {
    const defaultID = useId();
    const inputID = useMemo(() => rest?.id ?? defaultID, [rest?.id, defaultID]);
    const [isFocused, setIsFocused] = useState(false);
    const [isError, setIsError] = useState(!!error);
    const [displayError, setDisplayError] = useState(errorMessage ?? "");

    useEffect(() => {
      setIsError(!!fieldState?.error || !!error);
    }, [error, fieldState]);

    useEffect(() => {
      if (fieldState?.error?.message) {
        setDisplayError(fieldState.error.message);
      }
    }, [fieldState]);

    const textareaClasses = cn(
      "w-full p-2 border rounded resize-none bg-transparent",
      rest.className,
      isError ? "border-red-500 text-red-600" : "border-gray-300",
      reverse ? "text-white" : "",
    );

    return (
      <div className={rest.hidden ? "hidden" : wrapperClassNames}>
        <div className="block h-m" style={{ marginBottom: "8px" }}>
          {label && (
            <Label htmlFor={inputID} reverse={reverse} required={rest.required}>
              {label}
            </Label>
          )}
        </div>
        <textarea
          {...rest}
          {...field}
          ref={field?.ref ?? ref}
          name={name}
          id={inputID}
          rows={rest.rows ?? 4}
          onFocus={(event: FocusEvent<HTMLTextAreaElement>) => {
            setIsFocused(true);
            rest?.onFocus?.(event);
          }}
          onBlur={(event: FocusEvent<HTMLTextAreaElement>) => {
            setIsFocused(false);
            rest?.onBlur?.(event);
            field?.onBlur();
          }}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            rest?.onChange?.(event);
            field?.onChange(event);
            setIsError(!!error);
          }}
          className={textareaClasses}
          value={field?.value ?? rest?.value ?? ""}
        />
        <span
          className={`block h-m text-xs leading-[7px] font-secondary ${isError || error ? "text-[#B42318]" : reverse ? "text-gray-10" : "text-gray-70"}  pt-xs px-xxs`}
        >
          {isError || error ? displayError || errorMessage : note ? note : ""}
        </span>
      </div>
    );
  },
);

TextareaBase.displayName = "TextareaBase";
export default TextareaBase;
